import { AsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { filterType } from '../../../config/enum';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../../interface/filter.interface';
import { useAppDispatch } from '../../../redux/hooks';
import CustomCheckbox from '../../custom-check-box/CustomCheckBox';
import CustomDatePicker from '../../custom-date-range/CustomDatePicker';
import CustomDateRange from '../../custom-date-range/CustomDateRange';
import CustomInput from '../../custom-input/CustomInput';
import CustomTimeRange from '../../custom-time-input/CustomTimeRange';
import MultiSelectDropdown from '../../multi-select-field/MultiSelectField';
import SelectField from '../../select-field/SelectField';

interface CustomTableFilterProperties {
  filters: CommonFilterProperties[];
  filterListLoading: boolean;
  filtersToDispatch?: (payload: FilterUpdateProperties) => PayloadAction<FilterUpdateProperties>;
  filterListToDispatch?: AsyncThunk<any, void, any>;
}

/**
 * Function to dynamically create and apply custom filters
 * @returns
 */
function CustomTableFilter(properties: CustomTableFilterProperties) {
  const { filters, filterListLoading, filtersToDispatch, filterListToDispatch } = properties;

  const dispatch = useAppDispatch();
  const { t } = useTranslation('sharedComponent');
  const [selectedFilter, setSelectedFilter] = useState<string>();

  /**
   * Function to dispatch the updated filters
   * @param name
   * @param value
   */
  const handleChangeFilters = (name: string, value: string | number) => {
    if (filtersToDispatch) {
      dispatch(
        filtersToDispatch({
          key: name,
          value: value?.toString(),
          type: 'add',
        }),
      );
    }
  };

  /**
   * Function to dispatch the updated filters
   * @param name
   * @param value
   */
  const handleChangeDateFilters = (name: string, value: string | number) => {
    if (filtersToDispatch) {
      dispatch(
        filtersToDispatch({
          key: filterType.DATE_RANGE,
          value: value.toString(),
          type: 'add',
          subKey: name,
        }),
      );
    }
  };

  /**
   * Function to dispatch the updated filters
   * @param name
   * @param value
   */
  const handleAngleBracketFilters = (name: string, value: string | number) => {
    if (filtersToDispatch) {
      dispatch(
        filtersToDispatch({
          key: filterType.ANGLE_BRACKETS,
          value: value.toString(),
          type: 'add',
          subKey: name,
        }),
      );
    }
  };

  /**
   * Function to dispatch the updated filters
   * @param name
   * @param value
   */
  const handleMultiSelectField = (name: string, value: string[]) => {
    const updatedFilterValues = filters.find((filter) => filter.key === name)?.listOfValue;
    const labels = updatedFilterValues
      ? updatedFilterValues.filter((item) => value.includes(item.value)).map((item) => item.label)
      : [];

    if (filtersToDispatch) {
      dispatch(
        filtersToDispatch({
          key: name,
          value,
          type: 'add',
          valueToShow: labels,
        }),
      );
    }
  };

  useEffect(() => {
    const withoutDefaultFilters = filters.filter((filter) => !filter.searchBy);
    if (!selectedFilter && withoutDefaultFilters.length) {
      setSelectedFilter(withoutDefaultFilters[0].key);
    }
  }, [filters]);

  useEffect(() => {
    const withoutDefaultFilters = filters.filter(
      (filter) => !filter.searchBy && !filter.FEAddedFilter,
    );
    if (withoutDefaultFilters.length === 0 && filterListToDispatch) {
      dispatch(filterListToDispatch());
    }
  }, []);

  return (
    <div className="custom-table-filter-wrap">
      {filterListLoading ? (
        <div className="loader-container">
          <div className="lds-dual-ring" />
        </div>
      ) : (
        <div className="custom-table-filter-container">
          <div className="available-filter-headers">
            {filters
              .filter((filter) => !filter.searchBy)
              .map((filter) => (
                <div
                  className={
                    filter.key === selectedFilter ? 'filter-header active' : 'filter-header'
                  }
                  role="presentation"
                  onClick={() => setSelectedFilter(filter.key)}>
                  <span
                    className={filter.key === selectedFilter ? 'active-filter' : 'in-active-filter'}
                  />
                  <h6
                    className={
                      filter.applyFilter || filter.key === selectedFilter ? 'applied' : ''
                    }>
                    {t(filter.name)}
                  </h6>
                </div>
              ))}
          </div>
          {/* <div className="hr-line" /> */}
          <div className="selected-filter-value">
            {(() => {
              const filterToDisplay = filters.find((filter) => filter.key === selectedFilter);

              if (!filterToDisplay) return null;

              let valueToShow;
              if (filterToDisplay.type === filterType.CHECKBOX) {
                valueToShow = (
                  <div className="checkbox-input-wrap">
                    {filterToDisplay.listOfValue?.map((singleValue) => (
                      <CustomCheckbox
                        checkboxLabel={singleValue.label}
                        name={filterToDisplay.key}
                        value={filterToDisplay.value.includes(singleValue.value)}
                        onChangeValue={() =>
                          handleChangeFilters(filterToDisplay.key, singleValue.value)
                        }
                        fontSize="small"
                      />
                    ))}
                  </div>
                );
              } else if (filterToDisplay.type === filterType.DATE) {
                valueToShow = (
                  <div className="checkbox-input-wrap">
                    <CustomDatePicker
                      name={filterToDisplay.key}
                      placeholder={filterToDisplay.name}
                      value={filterToDisplay.value.toString()}
                      readOnly={false}
                      onChangeValue={handleChangeFilters}
                    />
                  </div>
                );
              } else if (filterToDisplay.type === filterType.DATE_RANGE) {
                valueToShow = (
                  <div className="date-range-input-wrap">
                    <CustomDateRange
                      onChangeValue={handleChangeDateFilters}
                      inputLabel={filterToDisplay.name}
                      value1={filterToDisplay?.value1 || ''}
                      value2={filterToDisplay?.value2 || ''}
                      name1={filterToDisplay?.key1 || 'startDate'}
                      name2={filterToDisplay?.key2 || 'endDate'}
                    />
                  </div>
                );
              } else if (filterToDisplay.type === filterType.TIME_RANGE) {
                valueToShow = (
                  <div className="time-range-input-wrap">
                    <CustomTimeRange
                      onChangeValue={handleChangeDateFilters}
                      inputLabel={filterToDisplay.name}
                      value1={filterToDisplay?.value1 || ''}
                      value2={filterToDisplay?.value2 || ''}
                      name1={filterToDisplay?.key1 || 'startTime'}
                      name2={filterToDisplay?.key2 || 'endTime'}
                    />
                  </div>
                );
              } else if (filterToDisplay.type === filterType.MULTISELECT) {
                valueToShow = (
                  <div className="multi-select-field-wrap">
                    <MultiSelectDropdown
                      name={filterToDisplay.key}
                      placeholderFilled={filterToDisplay.name}
                      title={filterToDisplay.name}
                      options={filterToDisplay?.listOfValue || []}
                      placeholder={filterToDisplay.name}
                      search
                      selectedValues={
                        Array.isArray(filterToDisplay.value) ? filterToDisplay.value : []
                      }
                      onSelectionChange={handleMultiSelectField}
                      fontSize="small"
                    />
                  </div>
                );
              } else if (filterToDisplay.type === filterType.ANGLE_BRACKETS) {
                valueToShow = (
                  <div className="angle-brackets-wrap">
                    <CustomInput
                      name={filterToDisplay.key1 || ''}
                      inputLabel={filterToDisplay.name}
                      type="text"
                      placeholder={t('enterValue')}
                      value={filterToDisplay.value1 || ''}
                      onChangeValue={handleAngleBracketFilters}
                      fontSize="small"
                    />
                    <SelectField
                      name={filterToDisplay.key2 || ''}
                      inputLabel={t('selectOperator')}
                      selectedValue={filterToDisplay.value2 || ''}
                      placeholder={t('selectOperator')}
                      onChangeValue={handleAngleBracketFilters}
                      options={filterToDisplay.listOfValue || []}
                      fontSize="small"
                    />
                  </div>
                );
              } else if (filterToDisplay.type === filterType.SELECT_FIELD) {
                valueToShow = (
                  <div className="select-field-wrap">
                    <SelectField
                      name={filterToDisplay.key}
                      options={filterToDisplay?.listOfValue || []}
                      placeholder={filterToDisplay.name}
                      inputLabel={filterToDisplay.name}
                      selectedValue={filterToDisplay?.value?.toString() || ''}
                      onChangeValue={handleChangeFilters}
                      fontSize="small"
                    />
                  </div>
                );
              }
              return valueToShow;
            })()}
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomTableFilter;
